/**
 * Close-record resolution for closed opportunities.
 *
 * Dynamics permits more than one `opportunityclose` activity per opportunity:
 * reopening a deal and closing it again mints a second record, and the first
 * one is left behind carrying whatever the rep typed the first time. Roughly
 * 1.5% of opportunities are in that state.
 *
 * "Most recent close wins" is the cheap rule and it is wrong often enough to
 * matter: the re-close is frequently an empty stub while the original holds
 * the loss narrative. The rule below is the one Lindsay's analysis settled on
 * (ADO #1449), and it needs the opportunity's own `actualclosedate`, which is
 * why this lives here rather than inside the query.
 *
 * Lives in the shared entity so the Ops-side pipeline insights (ADO #1448) can
 * reuse it rather than re-deriving a second, differently-wrong rule.
 */

import type { OpportunityClose } from '../api/opportunityApi';
import type { Opportunity } from '../model/types';

/** Day-precision compare: Dataverse date-only and datetime columns both show
 *  up here, and `actualend` carries a time while `actualclosedate` usually
 *  does not. Comparing the ISO date prefix avoids a spurious mismatch. */
function sameDay(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false;
  return a.slice(0, 10) === b.slice(0, 10);
}

function hasText(v: string | undefined): boolean {
  return typeof v === 'string' && v.trim().length > 0;
}

/**
 * Pick the one close record that represents this opportunity's closure.
 *
 * Order, per the spec on #1449:
 *   1. the record whose `actualend` falls on the opportunity's `actualclosedate`
 *   2. of those still tied, one with a non-blank description
 *   3. of those still tied, one with `dts_closureconfirmation` true
 *   4. otherwise the first remaining record
 *
 * Each step narrows the candidate set only when it would not empty it, so a
 * single-record opportunity (the other 98.5%) always returns that record.
 */
export function resolveCloseRecord(
  opp: Pick<Opportunity, 'actualCloseDate'>,
  closes: OpportunityClose[] | undefined,
): OpportunityClose | undefined {
  if (!closes || closes.length === 0) return undefined;
  if (closes.length === 1) return closes[0];

  const narrow = (
    pool: OpportunityClose[],
    predicate: (c: OpportunityClose) => boolean,
  ): OpportunityClose[] => {
    const kept = pool.filter(predicate);
    return kept.length > 0 ? kept : pool;
  };

  let pool = closes;
  pool = narrow(pool, (c) => sameDay(c.closeDate, opp.actualCloseDate));
  if (pool.length === 1) return pool[0];
  pool = narrow(pool, (c) => hasText(c.description));
  if (pool.length === 1) return pool[0];
  pool = narrow(pool, (c) => c.closureConfirmed === true);
  return pool[0];
}

/**
 * Whether a resolved close record actually documents WHY the deal closed.
 *
 * The distinction that matters on the Lost report: nearly every Lost
 * opportunity has a close record, but the large majority are empty stubs with
 * no reason and no narrative. A row with a stub is "no closure data on file",
 * not "closed for an unknown reason", and the report has to be able to tell
 * a reader which one it is looking at.
 */
export function hasClosureData(close: OpportunityClose | undefined): boolean {
  if (!close) return false;
  return (
    hasText(close.wonOrLossReason)
    || hasText(close.secondaryCloseReason)
    || hasText(close.description)
  );
}
